import { useMemo } from 'react';

import {
  EntityId,
  Asset,
  Worker,
  WorkerJPSavedResearch,
  SaveResearchType,
  ListSaveResearchPayloadType,
  WorkerPastExperience,
  WorkerPastExperienceInconsistency,
  WorkerReference,
  WorkerReport,
} from '@restworld/utility-types';

import { ENDPOINTS } from './../../endpoints';
import { Service } from './../base';
import dynamicAxios from './../../utils/axios';
import {
  ApplicationStatus,
  CreateAsset,
  IndexActiveApplications,
  IndexWorkers,
  ResearchStatus,
  SubscriptionStatus,
  WorkerFilterType,
  MissingCVTypePayload,
  SendAssetType,
  WorkerListItem,
  EmployerJobApplications,
  IndexWorkerPastExperiencesReturnType,
  IndexWorkerReferencesReturnType,
  CvParsingJobStatus,
  WorkerReferralConsents,
  WorkerReferralProgramInfo,
  WorkerReferralProgramStats,
} from './types';
import { Id, UploadPicture } from '../../utils/common_types';
import useServices from '../../hooks/useServices';
import { indexWorkersQuery } from './graphqlQueries';
import { indexEmployerJobApplicationsQuery } from '../useWorkerJobPositions/graphqlQueries';

class ServiceWorker extends Service {
  fetchWorkers({
    jobPositionId,
    limit,
    offset,
    order_by,
    filters,
    useCache,
  }: {
    jobPositionId?: string;
    limit: number;
    offset: number;
    order_by?: Array<string>;
    filters?: WorkerFilterType;
    useCache?: boolean;
  }) {
    const appliedFilters = filters || {};
    return this.axiosInstance.post<IndexWorkers>(ENDPOINTS.workers.graphql, {
      query: indexWorkersQuery,
      variables: { limit, offset, job_position_id: jobPositionId, ...appliedFilters, order_by },
    });
  }

  updateResearchStatus({ workerId, statusKey }: { workerId: EntityId; statusKey: string }) {
    return this.axiosInstance.patch<Worker>(ENDPOINTS.workers.updateResearchStatus, {
      worker_id: workerId,
      status_key: statusKey,
    });
  }

  fetchActiveApplications({ workerId }: { workerId: EntityId }) {
    return this.axiosInstance.get<IndexActiveApplications>(ENDPOINTS.workers.activeApplications, {
      params: {
        id: workerId,
      },
    });
  }

  fetchWorker(workerId: EntityId) {
    return this.axiosInstance.get<Worker>(`${ENDPOINTS.workers.root}/${workerId}`);
  }

  createWorker(workerData: Partial<Worker>) {
    return this.axiosInstance.post<{
      jwt?: string;
      worker?: {
        user_id?: string;
      };
    }>(`${ENDPOINTS.workers.createWorker}`, workerData, {
      headers: {
        'content-type': 'application/json',
      },
    });
  }

  updateWorker({ workerId, params }: { workerId: EntityId; params: Partial<Worker> }) {
    return this.axiosInstance.patch<Worker>(`${ENDPOINTS.workers.root}/${workerId}`, params);
  }

  sendMissingCVData(payload: MissingCVTypePayload) {
    return this.axiosInstance.post<{ id: string; jwt?: string }>(ENDPOINTS.workers.uploadCv_2, payload, {
      headers: {
        'content-type': 'application/json',
      },
    });
  }

  uploadCV({
    workerId,
    cv,
    jobPositionId,
    idToken,
  }: {
    workerId?: EntityId;
    cv: File;
    jobPositionId?: string;
    idToken?: string;
  }) {
    const formData = new FormData();
    if (workerId) formData.append('user_id', workerId);
    if (jobPositionId) formData.append('job_position_id', jobPositionId);
    // New-worker Google sign-up only: forward the Google ID token so the backend
    // re-verifies the identity server-side and takes email/name/google_id from the
    // verified token, never from raw client fields, before creating the worker.
    if (idToken) formData.append('id_token', idToken);
    formData.append('cv', cv);

    return this.axiosInstance.post<CreateAsset>(ENDPOINTS.workers.uploadCv, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  }

  deleteCV(workerId: EntityId) {
    return this.axiosInstance.delete<string>(ENDPOINTS.workers.deleteCv, {
      data: { user_id: workerId },
    });
  }

  sendEmailToWorker({ email, subject, content }: { email: string; subject: string; content: string }) {
    return this.axiosInstance.post<string>(ENDPOINTS.workers.sendEmail, {
      email,
      subject,
      content,
    });
  }

  sendAccessEmail(workerId: EntityId) {
    return this.axiosInstance.post<string>(ENDPOINTS.workers.sendAccessEmail, {
      worker_id: workerId,
    });
  }

  deleteWorker(workerId: EntityId) {
    return this.axiosInstance.delete<string>(ENDPOINTS.workers.root, {
      params: { id: workerId },
    });
  }

  fetchWorkerAsset(url: string) {
    return this.axiosInstance.get<Blob>(`${ENDPOINTS.workers.asset}`, {
      params: { url },
      responseType: 'blob',
    });
  }

  fetchResearchStatus() {
    return this.axiosInstance.get<ResearchStatus>(ENDPOINTS.workers.researchStatus);
  }

  fetchCvParsingJobStatus(workerId?: EntityId) {
    return this.axiosInstance.get<CvParsingJobStatus>(ENDPOINTS.workers.cvParsingJobStatus, {
      params: {
        id: workerId,
      },
    });
  }

  fetchBoostStatus(worker_id: string) {
    return this.axiosInstance.get<SubscriptionStatus>(`${ENDPOINTS.workers.boostStatus}`, {
      params: {
        id: worker_id,
      },
    });
  }

  fetchApplicationStatus(worker_id: string) {
    return this.axiosInstance.get<ApplicationStatus>(ENDPOINTS.workers.applicationStatus, {
      params: {
        worker_id,
      },
    });
  }

  fetchByNativeCVPublicUsername({ username, collect_metrics }: { username: string; collect_metrics: boolean }) {
    return this.axiosInstance.get<Worker>(`${ENDPOINTS.workers.nativeCV}/${username}`, {
      params: {
        collect_metrics,
      },
    });
  }

  checkEmailValidity(email: string) {
    return this.axiosInstance.get<string>(ENDPOINTS.workers.emailValidity, {
      params: {
        email,
      },
    });
  }

  sendAsset({ file, type }: SendAssetType) {
    const formData = new FormData();
    formData.append('asset', file);
    formData.append('type', type);
    return this.axiosInstance.post<{ url: string }>(ENDPOINTS.workers.asset, formData);
  }

  sendPicture({ email, picture }: { email: string; picture: File }) {
    const formData = new FormData();
    formData.append('email', email || '');
    formData.append('picture', picture);

    return this.axiosInstance.post<UploadPicture>(ENDPOINTS.workers.sendPicture, formData);
  }

  deleteAsset(asset: Asset) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.workers.asset}/${encode(asset.url ?? '')}`);
  }

  listSavedResearches({ limit, offset, email }: ListSaveResearchPayloadType) {
    return this.axiosInstance.get<WorkerJPSavedResearch[]>(ENDPOINTS.workers.savedResearches, {
      params: { limit, offset, email },
    });
  }

  saveJPResearch(params: SaveResearchType) {
    return this.axiosInstance.post<string>(ENDPOINTS.workers.savedResearch, params);
  }

  fetchSavedResearch({ researchId }: { researchId: number }) {
    return this.axiosInstance.get<WorkerJPSavedResearch>(ENDPOINTS.workers.savedResearch, {
      params: { id: researchId },
    });
  }

  updateSavedResearch(params: WorkerJPSavedResearch) {
    return this.axiosInstance.patch<string>(ENDPOINTS.workers.savedResearch, { ...params });
  }

  deleteSavedResearch({ researchId, email }: { researchId: number; email?: string }) {
    return this.axiosInstance.delete<string>(ENDPOINTS.workers.savedResearch, { params: { id: researchId, email } });
  }

  fetchSimilarWorkers({
    workerId,
    jobPositionId,
    limit,
    limitWorkerDistance,
    workerDistanceRadius,
  }: {
    workerId: EntityId;
    jobPositionId?: string;
    limit?: number;
    limitWorkerDistance?: boolean;
    workerDistanceRadius?: number;
  }) {
    return this.axiosInstance.get<WorkerListItem[]>(`${ENDPOINTS.workers.similarWorkers}/${workerId}`, {
      params: {
        limit,
        job_position_id: jobPositionId,
        limit_worker_distance: limitWorkerDistance,
        worker_distance_radius: workerDistanceRadius,
      },
    });
  }

  fetchSimilarWorkersUsingDirectSemanticSearch({
    jobPositionId,
    maxRestaurantDistance,
  }: {
    jobPositionId?: string;
    maxRestaurantDistance?: number;
  }) {
    return this.axiosInstance.get<WorkerListItem[]>(
      `${ENDPOINTS.workers.root}/job_positions/${jobPositionId}/similar`,
      { params: { max_restaurant_distance: maxRestaurantDistance } }
    );
  }

  fetchEmployerJobApplications({
    limit,
    offset,
    employerId,
    search_vector,
    spontaneous_applications,
    organizationId,
  }: {
    limit: number;
    offset: number;
    employerId?: string;
    organizationId?: string;
    search_vector?: string;
    spontaneous_applications?: boolean;
  }) {
    return this.axiosInstance.post<EmployerJobApplications>(ENDPOINTS.workerJobPositions.graphql, {
      query: indexEmployerJobApplicationsQuery,
      variables: {
        limit,
        offset,
        employer_id: employerId,
        search_vector,
        spontaneous_applications,
        organization_id: organizationId,
      },
    });
  }

  // Worker References related functions //
  listWorkerPastExperiences({ limit, offset, workerId }: { limit: number; offset: number; workerId?: string }) {
    return this.axiosInstance.get<IndexWorkerPastExperiencesReturnType>(ENDPOINTS.workers.workerPastExperiences, {
      params: {
        limit,
        offset,
        worker_id: workerId,
      },
    });
  }

  showWorkerPastExperience({ id }: { id: string }) {
    return this.axiosInstance.get<WorkerPastExperience>(`${ENDPOINTS.workers.workerPastExperiences}/${id}`);
  }

  updateWorkerPastExperience(params: { past_experiences: Partial<WorkerPastExperience>[] }) {
    return this.axiosInstance.patch<Id | string>(`${ENDPOINTS.workers.workerPastExperiences}`, params);
  }

  createWorkerPastExperienceInconsistency(params: Partial<WorkerPastExperienceInconsistency>) {
    return this.axiosInstance.post<Id>(ENDPOINTS.workers.workerPastExperienceInconsistencies, params);
  }

  listWorkerReferences({ limit, offset, workerId }: { limit: number; offset: number; workerId?: string }) {
    return this.axiosInstance.get<IndexWorkerReferencesReturnType>(ENDPOINTS.workers.references, {
      params: {
        limit,
        offset,
        worker_id: workerId,
      },
    });
  }

  createWorkerReference(params: Partial<WorkerReference>) {
    return this.axiosInstance.post<Id>(ENDPOINTS.workers.references, params);
  }

  updateWorkerReference(params: Partial<WorkerReference>) {
    return this.axiosInstance.patch<Id>(`${ENDPOINTS.workers.references}/${params.id}`, params);
  }

  fetchWorkerReports(workerId: EntityId) {
    return this.axiosInstance.get<WorkerReport[]>(`${ENDPOINTS.workers.root}/reports`, {
      params: { worker_id: workerId },
    });
  }

  createWorkerReport(workerReport: Partial<WorkerReport>) {
    return this.axiosInstance.post<Id>(`${ENDPOINTS.workers.root}/reports`, workerReport);
  }

  updateWorkerReport(workerReport: Partial<WorkerReport>) {
    return this.axiosInstance.patch<Id>(`${ENDPOINTS.workers.root}/reports/${workerReport.id}`, workerReport);
  }

  deleteWorkerReport(reportId: number) {
    return this.axiosInstance.delete(`${ENDPOINTS.workers.root}/reports/${reportId}`);
  }

  /** "Porta un locale" program state for the authenticated worker. */
  fetchReferralProgram() {
    return this.axiosInstance.get<WorkerReferralProgramInfo>(ENDPOINTS.workers.referralProgram);
  }

  /** Enrolls the authenticated worker. Both consents are mandatory: the API
   * answers 422 if either the contract acceptance or the specific approval of
   * the clauses under artt. 1341-1342 c.c. is missing. Idempotent. */
  enrollReferralProgram(consents: WorkerReferralConsents) {
    return this.axiosInstance.post<WorkerReferralProgramInfo>(ENDPOINTS.workers.referralProgramEnroll, consents);
  }

  /** Referral funnel stats for the authenticated worker's dashboard. */
  fetchReferralProgramStats() {
    return this.axiosInstance.get<WorkerReferralProgramStats>(ENDPOINTS.workers.referralProgramStats);
  }
}

export const workersService = ({ baseUrl, jwt }: { baseUrl: string; jwt?: string }) => {
  return new ServiceWorker(dynamicAxios(baseUrl, jwt));
};

export const encode = (url: string) =>
  encodeURIComponent(url)
    .replace(/!/g, '%21')
    .replace(/~/g, '%7E')
    .replace(/\*/g, '%2A')
    .replace(/'/g, '%27')
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29')
    .replace(/%20/g, '+');

export function useWorkersService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceWorker(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export default useWorkersService;

export * from './types';
